import type { Authentication, AuthMechanism, Credentials } from '@ekkogmbh/apisdk';

export class WebEamAuthMechanism implements AuthMechanism {
  public authentication: Authentication = { isAuthenticated: false };
  private e2eInitialized: boolean = false;
  private callback?: () => Promise<void>;

  constructor(
    private e2e: E2E | null,
    private readonly e2eSettings: E2ESettings,
  ) {}

  public setE2E(e2e: E2E): void {
    this.e2e = e2e;
  }

  private getE2E(): E2E {
    if (this.e2e === null) {
      throw new Error('WebEamAuthMechanism: E2E not set.');
    }

    return this.e2e;
  }

  public async authenticate(_?: Credentials, callback?: () => Promise<void>): Promise<void> {
    this.callback = callback;

    if (!this.e2eInitialized) {
      this.getE2E().init(this.e2eSettings, this.e2eInitCallback);
    } else {
      this.getE2E().fetchToken({}, undefined);
    }
  }

  public async invalidateAuthentication(): Promise<void> {
    console.log('WebEamAuthMechanism: invalidateAuthentication...');
    this.authentication.authToken = undefined;
    this.authentication.user = undefined;
    this.authentication.isAuthenticated = false;

    this.getE2E().invalidateToken();
  }

  protected e2eInitCallback = async (status: { code: number; message: string }): Promise<void> => {
    console.log('WebEamAuthMechanism: Running own E2E callback wrapper...');
    console.log(status);

    this.e2eInitialized = true;

    let success: boolean = false;

    switch (status.code) {
      case 1:
        // Token successfully acquired and stored in E2E.token.access_token | its details in E2E.token

        const token = this.getE2E().token;

        this.authentication.authToken = { token: token.access_token, refresh_token: token.refresh_token };
        this.authentication.isAuthenticated = true;

        success = true;
        console.log('WebEamAuthMechanism: Successfully retrieved WebEAM token:');
        console.log(token);
        break;
      case 2:
      // Couldn't acquire token via cookie and credentials weren't passed. Use E2E.fetchToken({username:'username', password:'password'});
      /* eslint no-fallthrough: 0 */
      case 3:
        // Server Error: Either bad endpoint configuration or wrong username and password provided - ask again and E2E.fetchToken({username:'username', password:'password'});
        // More details in status.message

        // Server Error or no cookie - check if we're NOT already returning from SSO
        // If URL hash contains token params, don't trigger another redirect
        const hasTokenInUrl = window.location.hash.includes('access_token') || window.location.hash.includes('error');
        if (!hasTokenInUrl) {
          this.getE2E().fetchToken({}, undefined);
        }
        break;
      case 4:
        // Connection Error: Server not responding or refused connection...
        break;
      case 5:
        // "E2E: Token successfully invalidated"
        success = true;
        break;
      default:
    }

    if (!success) {
      this.authentication.isAuthenticated = false;
      throw new Error(status.code + ' -- ' + status.message);
    }

    if (this.callback !== undefined) {
      console.log('WebEamAuthMechanism: Running original E2E callback...');
      await this.callback();
    }
  };
}
